import { setPriceText } from './dom-utils.js';

class StayAIQtySelector {
  constructor(widget) {
    this.widget = widget;
    this.product = this.safeParseJSON(widget.getAttribute('productData'));
    this.productFormSelector = widget.getAttribute('productFormId');
    this.currentVariant = null;
  }

  init() {
    if (!this.product?.variants?.length) return;
    this.currentVariant = this.getCurrentVariant();
    this.bindEvents();
    this.updateExternalPrices();
  }

  bindEvents() {
    document.addEventListener('quantity:updated', () =>
      this.updateExternalPrices()
    );
    window.addEventListener('stay-ai-subscription-changed', () =>
      this.updateExternalPrices()
    );
    document.addEventListener('variant:change', event => {
      if (event?.detail?.variant) {
        this.currentVariant = event.detail.variant;
      } else {
        this.currentVariant = this.getCurrentVariant();
      }
      this.updateExternalPrices();
    });

    const form = this.getProductForm();
    if (form) {
      form.addEventListener('change', event => {
        const target = event.target;
        if (
          target?.name === 'id' ||
          target?.name === 'quantity' ||
          target?.name === 'selling_plan'
        ) {
          this.currentVariant = this.getCurrentVariant();
          this.updateExternalPrices();
        }
      });
    }
  }

  getProductForm() {
    if (this.productFormSelector) {
      const explicit = document.querySelector(this.productFormSelector);
      if (explicit) return explicit;
    }

    const key = String(this.product?.id || '');
    const bySellingPlanKey = key
      ? document
          .querySelector(`[pdpAtcBtn-price][pdpAtcBtn-key="${key}"]`)
          ?.closest("form[action='/cart/add']")
      : null;
    if (bySellingPlanKey) return bySellingPlanKey;

    return document.querySelector("form[action='/cart/add']:not(.installment)");
  }

  getCurrentVariant() {
    const form = this.getProductForm();
    const variantId = form?.querySelector("[name='id']")?.value;
    if (variantId) {
      const variant = this.product.variants.find(
        v => String(v.id) === String(variantId)
      );
      if (variant) return variant;
    }
    return (
      this.product.variants.find(
        v =>
          String(v.id) ===
          String(this.product.selected_or_first_available_variant?.id)
      ) || this.product.variants[0]
    );
  }

  getCurrentQuantity() {
    const form = this.getProductForm();
    const qty = Number(new FormData(form).get('quantity') || 1);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  getSelectedSellingPlanId() {
    const form = this.getProductForm();
    const value = new FormData(form).get('selling_plan');
    return value ? String(value) : '';
  }

  isSaleActive() {
    if (!window.isSaleEnabled || !this.product?.tags?.length) return false;
    return this.product.tags.includes(window.saleEnabledTag);
  }

  calculateSalePrice(priceInCents) {
    const percentageOff = Number(window.saleDiscountPercentage || 0);
    const discount = 1 - percentageOff / 100;
    return Math.round(priceInCents * discount);
  }

  getBaseAndDisplayPrices() {
    const variant = this.currentVariant || this.getCurrentVariant();
    if (!variant) return null;

    const selectedSellingPlanId = this.getSelectedSellingPlanId();
    const selectedAllocation = variant.selling_plan_allocations?.find(
      spa => String(spa.selling_plan_id) === selectedSellingPlanId
    );

    const basePrice = selectedAllocation?.price ?? variant.price;
    const displayPrice = this.isSaleActive()
      ? this.calculateSalePrice(basePrice)
      : basePrice;

    let compareAt = 0;
    if (this.isSaleActive()) {
      compareAt = basePrice;
    } else if (
      variant.compare_at_price &&
      variant.compare_at_price > variant.price
    ) {
      compareAt = variant.compare_at_price;
    }

    return { displayPrice, compareAt };
  }

  formatMoney(cents) {
    if (
      window.theme?.Currency?.formatMoney &&
      window.theme?.settings?.moneyFormat
    ) {
      return window.theme.Currency.formatMoney(
        cents,
        window.theme.settings.moneyFormat
      );
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((cents || 0) / 100);
  }

  updateExternalPrices() {
    const prices = this.getBaseAndDisplayPrices();
    if (!prices) return;

    const quantity = this.getCurrentQuantity();
    const totalPrice = prices.displayPrice * quantity;
    const totalCompareAt = prices.compareAt * quantity;
    const key = String(this.product?.id || '');

    document
      .querySelectorAll(`[pdpAtcBtn-price][pdpAtcBtn-key="${key}"]`)
      .forEach(el => {
        setPriceText(el, this.formatMoney(totalPrice));
      });

    document
      .querySelectorAll(`[pdpAtcBtn-compare-at-price][pdpAtcBtn-key="${key}"]`)
      .forEach(el => {
        if (totalCompareAt > 0) {
          setPriceText(el, this.formatMoney(totalCompareAt));
          el.style.removeProperty('display');
        } else {
          el.style.display = 'none';
        }
      });
  }

  safeParseJSON(value) {
    try {
      return JSON.parse(value || '{}');
    } catch (_) {
      return null;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('purchase-options').forEach(widget => {
    new StayAIQtySelector(widget).init();
  });
});
